import { Fragment } from 'react'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { fillArray, take } from 'lib/array/arrayUtils'
import { getPlural } from 'lib/string/pluralize'
import switchFunc from 'lib/function/switchFunc'
import Heading from 'LuxKit/Typography/Heading'

import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineBedBunkIcon from 'LuxKit/Icons/line/LineBedBunkIcon'
import LineBedSingleIcon from 'LuxKit/Icons/line/LineBedSingleIcon'
import LineBedQueenIcon from 'LuxKit/Icons/line/LineBedQueenIcon'
import LineBedDoubleAltIcon from 'LuxKit/Icons/line/LineBedDoubleAltIcon'
import LineBedKingIcon from 'LuxKit/Icons/line/LineBedKingIcon'
import LineBedSofaIcon from 'LuxKit/Icons/line/LineBedSofaIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'

const BedroomGrid = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template-columns: 1fr 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

const BedroomTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: ${rem(160)};
  padding: ${rem(12)} ${rem(20)};
  background-color: ${(props) =>
    props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid
    ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const bedroomTypeToIcon = switchFunc<React.ReactNode, App.ConfigurationBedType>(
  {
    QueenBed: <LineBedQueenIcon size="XL" />,
    SingleBed: <LineBedSingleIcon size="XL" />,
    BunkBed: <LineBedBunkIcon size="XL" />,
    DoubleBed: <LineBedDoubleAltIcon size="XL" />,
    TwinXLBed: <LineBedDoubleAltIcon size="XL" />,
    TwinBed: <LineBedDoubleAltIcon size="XL" />,
    KingBed: <LineBedKingIcon size="XL" />,
    SofaBed: <LineBedSofaIcon size="XL" />,
    SofaBedExtra: <LineBedSofaIcon size="XL" />,
  },
  <LineBedIcon size="XL" />,
)

interface Props {
  roomsWithBeds: Array<App.RoomConfiguration>;
}

function PropertyRoomConfigurations(props: Props) {
  const { roomsWithBeds } = props

  const intl = useIntl()

  return (
    <>
      <Heading variant="heading4" as="h4">
        {intl.formatMessage({
          id: 'l1NL-XK_AZWJ-HC0NDie',
          defaultMessage: 'Where you’ll sleep',
          description: 'Heading for bedbank room type of bed configurations',
        })}
      </Heading>
      <BedroomGrid>
        {roomsWithBeds.map((bedroom, index) => (
          <BedroomTile key={index}>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {take(
                bedroom.beds.flatMap((bed) =>
                  fillArray(bed.count, () => bed.type),
                ),
                3,
              ).map((bedType, bedIndex) => (
                <Fragment key={bedIndex}>
                  {bedIndex < 2 && bedroomTypeToIcon(bedType)}
                  {/* At most we show 2 bed icons and after that we indicate there are more with a + */}
                  {bedIndex === 2 && <LinePlusIcon size="XS" />}
                </Fragment>
              ))}
            </Group>
            <div>
              <BodyText
                variant="large"
                weight="bold"
                colour="light-foreground-alt"
              >
                {intl.formatMessage(
                  {
                    id: 'Y3yH29_FvTAfSzRSf6J4',
                    defaultMessage: '{name} {number}',
                    description:
                      'Bedroom label with room name and number in villa property',
                  },
                  { name: bedroom.name, number: index + 1 },
                )}
              </BodyText>
              <BodyText variant="medium" colour="light-foreground-alt">
                {intl.formatList(
                  bedroom.beds.map((bed) =>
                    bed.count === 1 ?
                        intl.formatMessage(
                          {
                            id: '2EtPl7czcV5mUc6elhWR',
                            defaultMessage: '{count} {singular}',
                            description:
                            'Singular bed count and type in villa bedroom listing, e.g. "1 King Bed"',
                          },
                          { count: bed.count, singular: bed.name },
                        ) :
                        intl.formatMessage(
                          {
                            id: 'j1Uk2aZ-ESeX8nsdGGGd',
                            defaultMessage: '{count} {plural}',
                            description:
                            'Plural bed count and type in villa bedroom listing, e.g. "2 King Beds"',
                          },
                          { count: bed.count, plural: getPlural(bed.name, bed.count) },
                        ),
                  ),
                  { type: 'conjunction' },
                )}
              </BodyText>
            </div>
          </BedroomTile>
        ))}
      </BedroomGrid>
    </>
  )
}

export default PropertyRoomConfigurations
